import api from "./api";
import type { User } from "@leave-system/shared/types";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  employeeId?: string; // Optional, will be auto-generated
  departmentId: string;
  teamId?: string;
  position?: string;
  phoneNumber?: string;
  hireDate?: string;
}

export interface AuthResponse {
  user: User;
  message?: string;
}

export class AuthService {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<{ data: AuthResponse }>(
      "/api/v1/auth/login",
      credentials,
    );
    return response.data.data;
  }

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<{ data: AuthResponse }>(
      "/api/v1/auth/register",
      data,
    );
    return response.data.data;
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    await api.post("/api/v1/auth/logout");
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get<{ data: { user: User } }>(
        "/api/v1/auth/me",
      );
      return response.data.data.user;
    } catch (error: any) {
      // Return null if not authenticated (401) instead of throwing
      if (error.response?.status === 401) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Refresh user session
   */
  async refreshSession(): Promise<AuthResponse> {
    const response = await api.post<{ data: AuthResponse }>(
      "/api/v1/auth/refresh",
    );
    return response.data.data;
  }

  /**
   * Change password
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await api.post("/api/v1/auth/change-password", {
      currentPassword,
      newPassword,
    });
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<void> {
    await api.post("/api/v1/auth/forgot-password", { email });
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post("/api/v1/auth/reset-password", {
      token,
      newPassword,
    });
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    await api.post("/api/v1/auth/verify-email", { token });
  }

  /**
   * Resend verification email
   */
  async resendVerificationEmail(): Promise<void> {
    await api.post("/api/v1/auth/resend-verification");
  }

  /**
   * Generate a new employee ID
   */
  async generateEmployeeId(): Promise<string> {
    const response = await api.get<{ data: { employeeId: string } }>(
      "/api/v1/auth/generate-employee-id",
    );
    return response.data.data.employeeId;
  }
}

export const authService = new AuthService();
export default authService;
